import { Component, ElementRef, HostListener, Input, ViewChild, inject } from '@angular/core';
import { FormGroup, FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { BudgetUpdateModel } from 'src/app/models/budget-update.model';
import { BudgetModel } from 'src/app/models/budget.model';
import { InterestRateModel } from 'src/app/models/interestRate.model';
import { BudgetService } from 'src/app/services/budget.service';
import { ToastService } from 'src/app/services/toast.service';
import { MatFabButton } from '@angular/material/button';
import { DecimalPipe } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatInput } from '@angular/material/input';

@Component({
    selector: 'app-budget-model-lagune',
    templateUrl: './budget-model-lagune.component.html',
    styleUrls: ['./budget-model-lagune.component.scss'],
    imports: [MatFabButton, DecimalPipe, MatIcon, FormsModule, ReactiveFormsModule, MatFormField, MatInput]
})
export class BudgetModelLaguneComponent {
  budgetSvc = inject(BudgetService);
  activatedRoute = inject(ActivatedRoute);
  toastr = inject(ToastService);


  @ViewChild('contentToPrint', { static: false }) contentToPrint!: ElementRef;
  @ViewChild('numeroInput', { static: false }) numeroInput!: ElementRef;
  currentInterestRates: InterestRateModel[] = [];
  @Input() day: string = '';
  @Input() month: string = '';
  @Input() year: string = '';
  @Input() budget!: BudgetModel;
  @Input() isCompleted!: boolean;
  budgetId!: string;
  disableSubmit: boolean = false;

  @Input() interestForm = new FormGroup({
    minimumCashDownPayment: new FormControl(),
  });

  minPayReceived: number = 0;
  valCuota: number = 0;
  cuotaRedondeada: number = 0;
  @Input() fundingInstallmentsTemp: { id: string; price: number }[] = [];
  @Input() optionalAccessoriesTemp: { id: string; price: number | string }[] =
    [];
  @Input() installationMaterialsTemp: { id: string; value: string }[] = [];
  updateBudget: BudgetUpdateModel = {
    minimumCashDownPayment: 0,
    fundingInstallments: [],
    optionalAccessories: [],
    installationMaterials: [],
  };

  ngOnInit() {
    this.getAllInterest();
    this.minPayReceived = this.budget.minimumCashDownPayment;
    this.interestForm.controls.minimumCashDownPayment.valueChanges.subscribe(
      (val) => {
        this.minPayReceived = val;
        this.updateFundingInstallments();
      }
    );
  }

  getIsComplete() {
    return this.isCompleted ? 'print' : 'task';
  }

  getAccessoryModel(accessory: any): string | number {
    return accessory.isPromotionalAccessory ? 'BONIFICADO' : accessory.price;
  }

  ngAfterViewInit() {
    this.numeroInput.nativeElement.addEventListener('keyup', (event: any) => {
      // Prevenir incremento y decremento al presionar las flechas arriba y abajo
      if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        event.preventDefault();
      }
    });
  }

  validateOnlyPositiveNumbers(event: KeyboardEvent) {
    let key = event.key;
    //solo numeros y deshabilitar incrementos con flechas
    if (
      ['.', 'e', ',', '-', '+'].includes(key) ||
      key === 'ArrowUp' ||
      key === 'ArrowDown'
    ) {
      event.preventDefault();
    }
    // No numeros negativos
    if (this.interestForm.controls.minimumCashDownPayment.value < 1) {
      this.interestForm.controls.minimumCashDownPayment.patchValue(null);
    }

    if (
      this.interestForm.controls.minimumCashDownPayment.value >=
      this.budget.cashPrice
    ) {
      this.interestForm.controls.minimumCashDownPayment.patchValue(
        this.budget.cashPrice
      );
    }
  }

  onSubmit() {
    if (!this.isCompleted) {
      this.updateBudget.minimumCashDownPayment =
        this.interestForm.controls.minimumCashDownPayment.value ?? 0;
      this.updateBudget.fundingInstallments = [...this.fundingInstallmentsTemp];
      this.updateBudget.optionalAccessories = [...this.optionalAccessoriesTemp];
      this.updateBudget.installationMaterials = [
        ...this.installationMaterialsTemp,
      ];

      this.disableSubmit = true;
      this.budgetSvc
        .updateBudget(this.budget.id.toString(), this.updateBudget)
        .subscribe({
          next: () => {
            this.toastr.success(
              'Presupuesto actualizado',
              'El presupuesto se actualizó correctamente.'
            );
            this.printPDF();
            this.toastr.warning(
              'Generando impresión',
              'Generando el archivo de impresión, por favor no actualice el navegador.'
            );
          },
          error: (err) => {
            this.toastr.error('Error', 'No se pudo actualizar el presupuesto.');
          },
        });
    } else {
      this.printPDF();
      this.toastr.warning(
        'Generando impresión',
        'Generando el archivo de impresión, por favor no actualice el navegador.'
      );
    }
  }

  printPDF() {
    // Obtener el bloque que se quiere imprimir
    const DATA: any = this.contentToPrint.nativeElement;
    const doc = new jsPDF('p', 'pt', 'a4');
    const options = {
      background: 'white',
      scale: 3,
    };

    // Generar la imagen a imprimir
    html2canvas(DATA, options).then((canvas) => {
      const img = canvas.toDataURL('image/PNG');

      const bufferX = 0;
      const bufferY = 0;
      const imgProps = (doc as any).getImageProperties(img);
      const pdfWidth = doc.internal.pageSize.getWidth() - 2 * bufferX;
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

      doc.addImage(
        img,
        'PNG',
        bufferX,
        bufferY,
        pdfWidth,
        pdfHeight,
        undefined,
        'FAST'
      );

      // Obtenemos el borrador del documento a imprimir
      const blob = doc.output('blob');

      // Creamos una nueva ventana con la imagen
      const newWindow = window.open('');
      const blobURL = URL.createObjectURL(blob);
      newWindow?.document.write(
        `<iframe width='100%' height='100%' src='${blobURL}'></iframe>`
      );
    });
  }

  accesoryModified(id: string, newPrice: number) {
    this.optionalAccessoriesTemp = this.optionalAccessoriesTemp.map(
      (accesorio) => {
        if (accesorio.id == id) {
          return { ...accesorio, price: +newPrice };
        }
        return accesorio;
      }
    );
  }

  materialModified(id: string, value: string) {
    this.installationMaterialsTemp = this.installationMaterialsTemp.map(
      (material) => {
        if (material.id == id) {
          return { ...material, value: value };
        }
        return material;
      }
    );
  }

  getAllInterest() {
    this.budgetSvc.getAllInterestRate().subscribe((data) => {
      this.currentInterestRates = data.toSorted(
        (a, b) => a.installments - b.installments
      );
    });
  }

  payoutEstimator(plan: InterestRateModel) {
    const totalPrice = this.budget != undefined ? this.budget.cashPrice : 0;
    let cantCuotas = plan.installments;
    let interes = plan.interestRate / 100;

    this.valCuota =
      ((totalPrice - this.minPayReceived) * interes +
        (totalPrice - this.minPayReceived)) /
      cantCuotas;

    this.cuotaRedondeada = +this.valCuota.toFixed(2);
    return this.cuotaRedondeada;
  }

  updateFundingInstallments() {
    this.fundingInstallmentsTemp = [];

    this.budget.fundingInstallments.forEach((plan) => {
      if (plan.isActive) {
        const totalPrice = this.budget != undefined ? this.budget.cashPrice : 0;
        const cantCuotas = plan.installments;
        const interes = plan.interestRate / 100;

        const valCuota =
          ((totalPrice - this.minPayReceived) * interes +
            (totalPrice - this.minPayReceived)) /
          cantCuotas;

        const cuotaRedondeada = +valCuota.toFixed(2);

        const installment = {
          id: plan.id.toString(),
          price: cuotaRedondeada,
        };

        this.fundingInstallmentsTemp.push(installment);
      }
    });
  }

  @HostListener('document:keydown', ['$event'])
  captureCtrlP(event: KeyboardEvent) {
    if (event.ctrlKey && event.key === 'p') {
      event.preventDefault();
      this.onSubmit();
    }
  }
}
